/**
 * A/B Testing Utility
 * Manages variant assignment and tracking
 */

// Cookie management
const COOKIE_NAME = 'ab_test_variant';
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Set a cookie
 */
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

/**
 * Get a cookie
 */
function getCookie(name) {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length, cookie.length);
  }
  return null;
}

/**
 * Get or assign A/B test variant
 * Returns 'A' or 'B'
 */
export function getABVariant() {
  // Check if user already has a variant assigned
  let variant = getCookie(COOKIE_NAME);
  
  // Check URL override (for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const overrideVariant = urlParams.get('variant');
  if (overrideVariant === 'A' || overrideVariant === 'B') {
    variant = overrideVariant;
    setCookie(COOKIE_NAME, variant, COOKIE_EXPIRY_DAYS);
    return variant;
  }
  
  // If no variant assigned, randomly assign one
  if (!variant) {
    variant = Math.random() < 0.5 ? 'A' : 'B';
    setCookie(COOKIE_NAME, variant, COOKIE_EXPIRY_DAYS);
  }
  
  return variant;
}

/**
 * Track A/B test event to backend API and Google Analytics
 */
export async function trackABEvent(eventName, variant, additionalData = {}) {
  // Send to backend API
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const eventType = eventName === 'ab_test_view' ? 'impression' : 'conversion';
    
    await fetch(`${backendUrl}/api/ab-test/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        variant: variant,
        event_type: eventType,
        conversion_type: additionalData.conversion_type || null,
        page: additionalData.page || window.location.pathname,
        user_agent: navigator.userAgent
      })
    });
  } catch (error) {
    console.error('[A/B Test] Failed to track event to backend:', error);
  }
  
  // Send to Google Analytics if gtag is available
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      ab_variant: variant,
      ...additionalData
    });
  }
  
  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[A/B Test] ${eventName}`, { variant, ...additionalData });
  }
}

/**
 * Track conversion (sign-up click, registration, etc.)
 */
export function trackConversion(conversionType, variant) {
  trackABEvent('ab_test_conversion', variant, {
    conversion_type: conversionType
  });
}

/**
 * Track page view with variant
 */
export function trackVariantView(variant) {
  trackABEvent('ab_test_view', variant, {
    page: window.location.pathname
  });
}

/**
 * Get variant statistics from localStorage (client-side tracking)
 */
export function getABStats() {
  const stats = localStorage.getItem('ab_test_stats');
  return stats ? JSON.parse(stats) : { A: { views: 0, conversions: 0 }, B: { views: 0, conversions: 0 } };
}

/**
 * Update variant statistics
 */
export function updateABStats(variant, type = 'view') {
  const stats = getABStats();
  
  if (type === 'view') {
    stats[variant].views++;
  } else if (type === 'conversion') {
    stats[variant].conversions++;
  }
  
  localStorage.setItem('ab_test_stats', JSON.stringify(stats));
}
